import { Realtime } from 'ably';
import Spaces, { Space } from '@ably/spaces';
import { store } from '../../../redux/store';
import { setScheduleUpdatedBy } from '../../../redux/slices/ganttSlice';
import { sentryLogger } from '../../../monitor/monitor';
import { Member } from './types';
import { base } from '../../../services/base';

class CollaborativeSpace {
  static ablyClient: Realtime | null = null;
  static spacesClient: Spaces | null = null;
  static currentSpace: string | null = null;
  static space: Space | null = null;
  static editingSpace: string | null = null;

  static init() {
    if (!this.ablyClient) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        this.ablyClient = new Realtime({
          key: base.ablyKey,
          clientId:
            user?.email || `guest-${Math.random().toString(36).slice(2)}`
        });

        this.spacesClient = new Spaces(this.ablyClient);
      } catch (error) {
        sentryLogger.error('Error on init CollaborativeSpace', { error });
      }
    }
  }

  static async setCurrentSpace(spaceName: string) {
    try {
      this.currentSpace = spaceName;
      this.space = await this.spacesClient?.get(spaceName);

      this.space?.channel?.subscribe('saved-event', async (message) => {
        const member = message.data.member;
        const self = await this.space?.members.getSelf();
        const sentByOtherMember = self.connectionId !== member.connectionId;

        store.dispatch(
          setScheduleUpdatedBy({
            member: sentByOtherMember ? member : null,
            updatedAt: sentByOtherMember ? message.createdAt : null
          })
        );
      });

      return this.space;
    } catch (error) {
      sentryLogger.error('Error on setCurrentSpace', { error });
    }
  }

  static async leaveSpace() {
    if (this.currentSpace && this.space) {
      try {
        await this.space.channel?.unsubscribe();
        await this.space.leave();
        this.currentSpace = null;
        this.space = null;
      } catch (error) {
        sentryLogger.error('Error on leaveSpace', { error });
      }
    }
  }

  static async destroy() {
    try {
      await this.leaveSpace();

      if (this.ablyClient) {
        await this.ablyClient.close();
        this.ablyClient = null;
        this.spacesClient = null;
        this.editingSpace = null;
      }
    } catch (error) {
      sentryLogger.error('Error on destroy CollaborativeSpace', { error });
    }
  }

  static async notifyIsEditing() {
    if (!this.space || this.editingSpace === this.currentSpace) {
      return;
    }

    try {
      const self = await this.space?.members.getSelf();
      if (!self) {
        return;
      }

      await this.space.updateProfileData((currentProfile) => ({
        ...currentProfile,
        isEditing: true
      }));
      this.editingSpace = this.currentSpace;
    } catch (error) {
      sentryLogger.error('Error on notifyIsEditing', { error });
    }
  }

  static async notifySaved() {
    if (!this.space) {
      return;
    }

    try {
      const member = (await this.space?.members.getSelf()) as Member;
      await this.space.channel.publish('saved-event', { member });
      await this.space.updateProfileData((currentProfile) => ({
        ...currentProfile,
        isEditing: false
      }));
    } catch (error) {
      sentryLogger.error('Error on notifySaved', { error });
    }
  }
}

export { CollaborativeSpace };
